import { useState } from 'react';
import { type Product } from "@shared/schema";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShoppingCart, ChevronDown, ChevronUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface ProductCardWithChineseProps {
  product: Product;
  compact?: boolean;
}

export function ProductCardWithChinese({ product, compact = false }: ProductCardWithChineseProps) {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const savings = ((product.retailPrice - product.ourPrice) / product.retailPrice * 100).toFixed(0);

  const toggleExpanded = () => {
    if (product.detailedDescription) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <Card
      className={`${isExpanded ? '' : 'h-full'} flex flex-col overflow-hidden transition-all duration-200 hover:shadow-lg relative group border border-gray-200 hover:border-emerald-200`}
    >
      {/* Image Section with Chinese Text */}
      <div className={`${compact ? 'h-28' : 'h-36'} bg-gray-100 overflow-hidden flex`}>
        {/* Chinese Text Column */}
        {product.chineseName && (
          <div
            className={`${compact ? 'w-7' : 'w-8'} h-full flex-shrink-0 flex items-center justify-center py-0.5 px-0 bg-gradient-to-b from-emerald-100 to-emerald-200`}
          >
            <div
              className={`font-bold ${compact ? 'text-lg' : 'text-xl'} text-emerald-800`}
              style={{
                writingMode: 'vertical-rl',
                textOrientation: 'upright',
                letterSpacing: '0.02em'
              }}
            >
              {product.chineseName}
            </div>
          </div>
        )}

        {/* Image Container */}
        <div className="flex-1 relative">
          {/* Savings Badge */}
          {savings !== "0" && (
            <Badge
              className="absolute top-1 right-1 z-10 font-semibold text-xs px-2 py-0.5 bg-emerald-600 text-white shadow-sm"
            >
              Save {savings}%
            </Badge>
          )}

          {/* Loading State */}
          {imageLoading && !imageError && (
            <div className="absolute inset-0 bg-gray-200 animate-pulse" />
          )}

          {/* Product Image */}
          {product.imageUrl && !imageError ? (
            <img
              src={product.imageUrl}
              alt={product.description}
              onError={() => setImageError(true)}
              onLoad={() => setImageLoading(false)}
              className={`
                w-full h-full object-cover
                ${imageLoading ? 'invisible' : 'visible'}
              `}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200">
              <ShoppingCart className="w-10 h-10 text-gray-400" />
            </div>
          )}
        </div>
      </div>

      {/* Content Section - More Condensed */}
      <CardContent className="flex-1 p-2 flex flex-col">
        {/* Product Name */}
        <h3 className={`
          font-semibold text-gray-900 leading-tight
          ${compact ? 'text-sm line-clamp-2' : 'text-base line-clamp-2'}
        `}>
          {product.description}
        </h3>

        {/* Price Section */}
        <div className="mt-auto pt-1">
          {/* Prices on same row */}
          <div className="flex items-baseline justify-between">
            {/* Strikethrough Price - Left, Red */}
            <span className={`text-red-500 line-through ${compact ? 'text-sm' : 'text-base'}`}>
              ${product.retailPrice.toFixed(2)}
            </span>

            {/* Current Price - Right, Large Green */}
            <span className={`font-black text-emerald-600 ${compact ? 'text-2xl' : 'text-3xl'}`}>
              ${product.ourPrice.toFixed(2)}
            </span>
          </div>
          {/* Unit below */}
          <div className="text-right">
            <span className="text-gray-500 text-xs uppercase">{product.size}</span>
          </div>
        </div>

        {/* MORE Button - Only show if detailedDescription exists */}
        {product.detailedDescription && (
          <Button
            onClick={toggleExpanded}
            variant="outline"
            size="sm"
            className="w-full mt-1.5 text-xs h-7"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-3 h-3 mr-1" />
                Less
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3 mr-1" />
                More
              </>
            )}
          </Button>
        )}

        {/* Expanded Content */}
        <AnimatePresence>
          {isExpanded && product.detailedDescription && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="pt-2 pb-1 border-t mt-2">
                <div className="text-xs text-gray-600 whitespace-pre-wrap">
                  {product.detailedDescription}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}
